import api from "@/lib/api";
import { buildCondEval } from "@/lib/condFormat";
import { t } from "@/lib/i18n";
import { getFormatDefaults } from "@/lib/chartTheme";
import { computeClientLevels, buildPivotModel, splitPivotDims } from "@/lib/pivotEngine";
import { fmtDatePattern, sortRows } from "@/components/bi/DataTable";
import { applyColumnFilters } from "@/lib/columnFilter";
import { expandTableInstances, tableColumnOrder } from "@/lib/tableColumns";
import { applyQuickCalcs } from "@/lib/quickCalcs";

const CUR_SYM = { USD: "$", EUR: "€", GBP: "£", INR: "₹", AED: "AED ", SGD: "S$", MYR: "RM", KES: "KSh ", NGN: "₦", SAR: "SAR ", QAR: "QAR ", ZAR: "R" };

// Item 20: Indian 2-2-3 digit grouping when the effective regional number system is "indian"
function groupedBase(dec) {
  const d = dec > 0 ? "." + "0".repeat(Math.min(6, dec)) : "";
  if (getFormatDefaults().numberSystem === "indian") return { base: "#,##0" + d, indian: `[>=10000000]##\\,##\\,##\\,##0${d};[>=100000]##\\,##\\,##0${d};#,##0${d}` };
  return { base: "#,##0" + d, indian: null };
}

function numFmtOf(fmt = {}) {
  const dec = fmt.decimals ?? (["currency", "accounting"].includes(fmt.type) ? 2 : fmt.type === "percent" ? 1 : 2);
  const { base, indian } = groupedBase(dec);
  const cur = fmt.currency || getFormatDefaults().currency;
  if (fmt.type === "accounting") {
    const sym = CUR_SYM[cur] || cur || "$";
    return `"${sym}"${base};("${sym}"${base})`;
  }
  if (fmt.type === "scientific") return "0." + "0".repeat(Math.max(1, Math.min(6, dec))) + "E+00";
  if (fmt.type === "fraction") return "# ?/?";
  if (fmt.type === "duration") return "[h]:mm:ss";
  let core;
  if (fmt.type === "currency") core = `"${CUR_SYM[cur] || cur || "$"}"${base}`;
  else if (fmt.type === "percent") core = base + "%";
  else core = indian || base;
  return fmt.negStyle === "paren" ? `${core};(${core})` : core;
}

function fxToStyle(fx) {
  if (!fx) return null;
  const s = {};
  if (fx.bg && String(fx.bg).startsWith("#")) s.bg = fx.bg;
  if (fx.color) s.color = fx.color;
  if (fx.bold) s.bold = true;
  if (fx.italic) s.italic = true;
  if (fx.underline) s.underline = true;
  if (fx.strike) s.strike = true;
  return Object.keys(s).length ? s : null;
}

function specStyle(f = {}) {
  const s = {};
  ["bg", "color", "bold", "italic", "underline", "align", "valign", "wrap", "size", "family"].forEach((k) => { if (f[k]) s[k] = f[k]; });
  if (f.border?.color) s.borderColor = f.border.color;
  return s;
}

// ---- Data Table -> styled sheet (item 24/25 export parity) ----
function buildTableSheet(widget, raw) {
  const cfg = widget.config || {};
  const t = cfg.options?.tableOpts || {};
  // Item 24: duplicate column instances + "show value as" + author column order, exactly as on screen
  const data = applyQuickCalcs(expandTableInstances(raw, cfg), cfg);
  const hidden = new Set(t.hidden || []);
  const ordered = tableColumnOrder(cfg, data.columns || []);
  const columns = (ordered || data.columns || []).filter((c) => !hidden.has(c));
  const measMeta = {}; (data.meta?.measures || []).forEach((m) => { measMeta[m.key] = m; });
  const dimMeta = {}; (data.meta?.dimensions || []).forEach((d) => { dimMeta[d.key] = d; });
  const ev = buildCondEval(widget.config?.options?.condRules, data.rows);
  const st = (c) => t.styles?.[c] || {};
  const cols = columns.map((c) => ({
    label: measMeta[c]?.label || dimMeta[c]?.label || c.replace(/_/g, " "),
    width: st(c).width || undefined,
    style: {
      ...specStyle(t.values || {}),
      ...specStyle(st(c)),
      ...(measMeta[c] || st(c).format ? { numFmt: numFmtOf({ ...(measMeta[c]?.format || {}), ...(st(c).format || {}) }) } : {}),
      ...(!st(c).align && measMeta[c] ? { align: "right" } : {}),
    },
  }));
  // Items 26/27 export parity: saved header filters + default sort travel into the sheet
  let srcRows = applyColumnFilters(data.rows || [], t.colFilters);
  if (t.defaultSort?.col) srcRows = sortRows([...srcRows], t.defaultSort.col, t.defaultSort.dir, !!measMeta[t.defaultSort.col] || srcRows.some((r) => typeof r[t.defaultSort.col] === "number"));
  const rows = srcRows.map((r) => {
    const fxRow = ev ? ev.row(r) : null;
    return columns.map((c) => {
      const fx = ev ? ev.cell(r, c) : null;
      let v = r[c];
      const cs = st(c);
      if (v == null || v === "") v = cs.nullDisplay || "";
      else if (cs.dateFormat) v = fmtDatePattern(v, cs.dateFormat) ?? v;
      const s = { ...(fxToStyle(fxRow) || {}), ...(fxToStyle(fx) || {}) };
      return Object.keys(s).length ? { v, s } : { v };
    });
  });
  return {
    name: widget.title || "Table",
    columns: cols,
    rows,
    header_style: { bold: true, bg: "#EEF2F7", ...specStyle(t.header || {}) },
  };
}

// ---- Pivot -> styled sheet, laid out like the on-screen pivot (fully expanded) ----
function buildPivotSheet(widget, data) {
  const config = widget.config || {};
  const opts = config.options?.pivotOpts || {};
  const columns = data.columns || [];
  const { rowDims, colDims } = splitPivotDims(config, columns);
  const dimSet = new Set([...rowDims, ...colDims]);
  const measureKeys = columns.filter((c) => !dimSet.has(c));
  const measMeta = {}; (data.meta?.measures || []).forEach((m) => { measMeta[m.key] = m; });
  const labelFor = (c) => measMeta[c]?.label || c.replace(/_/g, " ");
  const aggBy = {};
  (config.measures || []).forEach((m) => {
    const k = m.key || m.alias || `${m.agg || "sum"}_${m.field}`;
    aggBy[k] = ["avg", "min", "max"].includes(m.agg) ? m.agg : "sum";
  });
  const pivotRows = applyColumnFilters(applyColumnFilters(data.rows || [], opts.fieldFilters), opts.measureFilters);
  const levels = computeClientLevels(pivotRows, rowDims, colDims, measureKeys, aggBy);
  const model = buildPivotModel({ levels, rowDims, colDims, measureKeys, sortBy: opts.sortBy, sortDir: opts.sortDir, sortMeasure: opts.sortMeasure, levelSort: opts.levelSort });
  const classic = opts.layout === "classic" || opts.layout === "tabular";
  const rowSubtotals = opts.rowSubtotals !== false && rowDims.length > 1;
  const grandRow = opts.grandRow !== false;
  const ev = buildCondEval(config.options?.condRules, data.rows);
  const R = rowDims.length;
  const leafCols = model.colTuples || [[]];

  const headerCols = [];
  if (classic) rowDims.forEach((d) => headerCols.push({ label: labelFor(d), width: 140 }));
  else headerCols.push({ label: rowDims.map(labelFor).join(" / "), width: 180 });
  leafCols.forEach((cp) => measureKeys.forEach((m) => headerCols.push({
    label: [...cp, labelFor(m)].filter(Boolean).join(" / "),
    width: 110,
    style: { align: "right", numFmt: numFmtOf(measMeta[m]?.format || {}) },
  })));

  const subF = opts.subtotalFmt || {};
  const grandF = opts.grandFmt || {};
  const subStyle = { bold: subF.bold !== false, ...(subF.bg ? { bg: subF.bg } : { bg: "#F3F6FA" }), ...(subF.color ? { color: subF.color } : {}) };
  const grandStyle = { bold: grandF.bold !== false, ...(grandF.bg ? { bg: grandF.bg } : { bg: "#E8EEF5" }), ...(grandF.color ? { color: grandF.color } : {}) };

  const rows = [];
  const outlines = [];  // Excel outline level per row (real collapsible groups)
  const valueCells = (path, style) => {
    const cells = [];
    leafCols.forEach((cp) => measureKeys.forEach((m) => {
      const v = model.getCell(path, cp, m);
      let s = style ? { ...style } : null;
      if (!style && path.length === R && cp.length === colDims.length && v != null && ev) {
        s = fxToStyle(ev.cell({ [m]: v }, m) || ev.cell({ value: v }, "value"));
      }
      cells.push(s ? { v: v ?? "", s } : { v: v ?? "" });
    }));
    return cells;
  };
  const pushNode = (node, depth) => {
    if (depth === R) {
      const labels = classic ? node.path.map((x) => ({ v: x })) : [{ v: `${"    ".repeat(Math.max(0, depth - 1))}${node.value ?? ""}` }];
      rows.push([...labels, ...valueCells(node.path, null)]);
      outlines.push(Math.max(0, R - 1));
      return;
    }
    (node.children || []).forEach((k) => pushNode(k, depth + 1));
    if (rowSubtotals) {
      const labels = classic
        ? Array.from({ length: R }, (_, d) => ({ v: d === depth - 1 ? `${node.path[d]} ${t("total")}` : "", s: subStyle }))
        : [{ v: `${"    ".repeat(Math.max(0, depth - 1))}${node.value} ${t("total")}`, s: subStyle }];
      rows.push([...labels, ...valueCells(node.path, subStyle)]);
      outlines.push(Math.max(0, depth - 1));
    }
  };
  (model.rowTree || []).forEach((n) => pushNode(n, 1));
  if (grandRow) {
    const labels = classic
      ? Array.from({ length: R }, (_, d) => ({ v: d === 0 ? t("grand_total") : "", s: grandStyle }))
      : [{ v: t("grand_total"), s: grandStyle }];
    rows.push([...labels, ...valueCells([], grandStyle)]);
    outlines.push(0);
  }
  return {
    name: widget.title || "Pivot",
    columns: headerCols,
    rows,
    row_outlines: R > 1 ? outlines : undefined,
    header_style: { bold: true, bg: "#EEF2F7", ...specStyle(opts.headerFmt || {}) },
    value_style: specStyle(opts.valuesFmt || {}),
  };
}

// Fetch-free entry: caller supplies the widget's query result rows.
// Item 26: works for every widget type (charts/KPIs export as a styled value sheet) and for
// shared/published links via the token-gated public export route.
export async function exportStyledExcel(widget, data, publicToken = null) {
  const sheet = widget.type === "pivot" ? buildPivotSheet(widget, data) : buildTableSheet(widget, data);
  const filename = (widget.title || widget.type || "export").replace(/\s+/g, "_");
  const res = publicToken
    ? await api.post("/public/export/styled-xlsx", { token: publicToken, filename, sheets: [sheet] },
      { responseType: "blob", timeout: 60000 })
    : await api.post("/export/styled-xlsx", { filename, sheets: [sheet] },
      { responseType: "blob", timeout: 60000 });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}
